import { useEffect, useRef } from "react";

import { getChangedFields, isEqual } from "../../util";
import { sanitiseTableRowUpdate } from "./sanitiseTableRowUpdate";

const isPlainObject = value =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const getMutationPayload = result =>
  Object.values(result?.data || {}).find(
    value =>
      isPlainObject(value) &&
      Object.prototype.hasOwnProperty.call(value, "success"),
  );

/**
 * Scalar fields of the saved record returned by the mutation, limited to keys
 * the row already has, so server-derived values (e.g. a recalculated rate)
 * replace the stale ones in the row.
 */
const getSavedRecordScalarFields = (result, { id, rowKeys }) => {
  const payload = getMutationPayload(result);
  const savedRecord = Object.values(payload || {}).find(
    value => isPlainObject(value) && value.id != null && String(value.id) === String(id),
  );

  return Object.fromEntries(
    Object.entries(savedRecord || {}).filter(
      ([key, value]) => rowKeys.includes(key) && !isPlainObject(value),
    ),
  );
};

const getMutationPayloadError = result => {
  const payload = getMutationPayload(result);

  if (payload?.success !== false) {
    return null;
  }

  const error = payload?.error;
  if (error instanceof Error) {
    return error;
  }

  const message =
    (typeof error === "string" && error) ||
    error?.message ||
    "The update could not be saved";

  return new Error(message);
};

/**
 * Sends one row update and returns the scalar fields of the saved record
 * from the response, or an empty object when nothing was saved.
 */
const submitRowUpdate = async (refs, submittedValues) => {
  const {
    headersRef,
    onMutationErrorRef,
    onMutationSuccessRef,
    rowRef,
    savedValuesRef,
    updateMutationRef,
  } = refs;
  const changedFields = getChangedFields(
    submittedValues,
    savedValuesRef.current,
  );
  const pruned = sanitiseTableRowUpdate(changedFields, headersRef.current);

  if (Object.keys(pruned).length === 0 || !updateMutationRef.current) {
    return {};
  }

  const updatedRow = {
    rowId: submittedValues?.id,
    fieldNames: Object.keys(pruned),
  };

  try {
    const result = await updateMutationRef.current({
      variables: {
        input: {
          id: submittedValues?.id,
          ...pruned,
        },
      },
    });
    const payloadError = getMutationPayloadError(result);

    if (payloadError) {
      onMutationErrorRef.current?.(payloadError, updatedRow);
      return {};
    }

    savedValuesRef.current = submittedValues;
    onMutationSuccessRef.current?.(updatedRow);
    return getSavedRecordScalarFields(result, {
      id: submittedValues?.id,
      rowKeys: Object.keys(rowRef.current || {}),
    });
  } catch (error) {
    onMutationErrorRef.current?.(error, updatedRow);
    return {};
  }
};

const processNextRowUpdate = async (refs, savedRecordFields = {}) => {
  const {
    formikRef,
    latestValuesRef,
    pendingValuesRef,
    rowRef,
    savedValuesRef,
    updateInProgressRef,
  } = refs;
  const submittedValues = pendingValuesRef.current;
  pendingValuesRef.current = null;

  if (!submittedValues) {
    updateInProgressRef.current = false;
    return;
  }

  const nextSavedRecordFields = {
    ...savedRecordFields,
    ...(await submitRowUpdate(refs, submittedValues)),
  };

  if (pendingValuesRef.current) {
    await processNextRowUpdate(refs, nextSavedRecordFields);
    return;
  }

  updateInProgressRef.current = false;

  if (isEqual(latestValuesRef.current, savedValuesRef.current)) {
    const settledValues = {
      ...rowRef.current,
      ...latestValuesRef.current,
      ...nextSavedRecordFields,
    };
    savedValuesRef.current = settledValues;
    formikRef.current?.resetForm({ values: settledValues });
  }
};

/**
 * Sends at most one mutation at a time and coalesces pending edits to the
 * latest row values. Server refreshes are applied only when no local draft
 * or update is pending.
 */
const useSequentialRowUpdates = ({
  debouncedValues,
  enabled,
  formik,
  headers,
  onMutationError,
  onMutationSuccess,
  row,
  updateMutation,
}) => {
  const formikRef = useRef(formik);
  const headersRef = useRef(headers);
  const latestValuesRef = useRef(formik.values);
  const lastQueuedValuesRef = useRef(row);
  const onMutationErrorRef = useRef(onMutationError);
  const onMutationSuccessRef = useRef(onMutationSuccess);
  const pendingValuesRef = useRef(null);
  const rowRef = useRef(row);
  const savedValuesRef = useRef(row);
  const updateInProgressRef = useRef(false);
  const updateMutationRef = useRef(updateMutation);

  formikRef.current = formik;
  headersRef.current = headers;
  latestValuesRef.current = formik.values;
  onMutationErrorRef.current = onMutationError;
  onMutationSuccessRef.current = onMutationSuccess;
  rowRef.current = row;
  updateMutationRef.current = updateMutation;

  useEffect(() => {
    if (
      !enabled ||
      updateInProgressRef.current ||
      pendingValuesRef.current ||
      !isEqual(latestValuesRef.current, savedValuesRef.current)
    ) {
      return;
    }

    savedValuesRef.current = row;
    lastQueuedValuesRef.current = row;
    formikRef.current?.resetForm({ values: { ...row } });
  }, [enabled, row]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const nextChangedFields = getChangedFields(
      debouncedValues || {},
      savedValuesRef.current || {},
    );
    const pruned = sanitiseTableRowUpdate(nextChangedFields, headers);
    if (
      Object.keys(pruned).length === 0 ||
      isEqual(debouncedValues, lastQueuedValuesRef.current)
    ) {
      return;
    }

    pendingValuesRef.current = debouncedValues;
    lastQueuedValuesRef.current = debouncedValues;

    if (!updateInProgressRef.current) {
      updateInProgressRef.current = true;
      processNextRowUpdate({
        formikRef,
        headersRef,
        latestValuesRef,
        onMutationErrorRef,
        onMutationSuccessRef,
        pendingValuesRef,
        rowRef,
        savedValuesRef,
        updateInProgressRef,
        updateMutationRef,
      });
    }
  }, [debouncedValues, enabled, headers]);
};

export default useSequentialRowUpdates;
